import { inject, provide, type InjectionKey } from "vue";

/**
 * A barrier the "proceed" action can await before it reads the form values (LSP-16816).
 *
 * `commitTypedAutocompleteValue` is async: when a pasted value cannot be resolved against
 * already-fetched options it awaits a real lookup. The browser fires `blur` before `click`, so a user
 * who pastes a job number and clicks Run straight away reaches the proceed guard while that commit is
 * still in flight — the guard reads `formValues` synchronously, sees nothing, and the button appears
 * dead until a second click. That is the same failure as the reported bug (a value that is on screen
 * but not committed), just relocated to the click-immediately variant of the same paste flow.
 *
 * `flushAutocompleteCommits` returns whether it can VOUCH for the form. The caller must refuse to read
 * it otherwise: proceeding on a false verdict is not safe even when validity passes, because
 * `handleAutocompleteSearch` deliberately keeps a field's previous valid selection in `formValues`
 * while a new lookup runs — so validity can read true off a value the user has already replaced on
 * screen. Every "no" below exists because reporting success there submitted stale data.
 *
 * ONE BARRIER PER DIALOG, via provide/inject. This was module-level state, and the rationale written
 * here was that a dialog mounts N renderers so per-instance plumbing would have to drain an array of
 * refs. provide/inject answers that directly — the dialog owns one barrier and every renderer under it
 * injects the SAME one — while fixing what module scope broke: `RunAgentDialog` is mounted by
 * `ScribeFab` app-wide AND by `SmartPackSelector` inside five Action components, none behind a `v-if`,
 * so several are resident at once. Sharing one module meant a dialog closing called `clear()` on
 * another dialog's in-flight commits: they leave `pendingCommits` WITHOUT settling, so the live
 * dialog's next flush saw an empty set and vouched for a form still mid-commit. That is precisely the
 * bug this barrier exists to prevent, reintroduced one layer up.
 */
export type AutocompleteCommitBarrier = {
	/** Track a commit so `flush` waits for it. */
	track(commit: Promise<unknown>): void;
	/** Resolve once no commit is in flight; FALSE means the form must not be read. */
	flush(): Promise<boolean>;
	/** Drop tracked commits on teardown. */
	clear(): void;
	/**
	 * Stop waiting on ONE commit, for an owner that is going away while its siblings stay.
	 *
	 * `clear` is the whole-dialog verb and would drop other renderers' live work. A renderer unmounted
	 * by a step change needs the narrow version: its form is gone and it has invalidated its own
	 * contexts, so its commit can no longer write anything — leaving it tracked only lets it time out
	 * and mark the barrier stuck, refusing a proceed on a form that is not even on screen.
	 */
	untrack(commit: Promise<unknown>): void;
	/**
	 * Register a live predicate saying whether this renderer currently has entries it could not resolve,
	 * and get back an unregister.
	 *
	 * The barrier is the "can we vouch for this form" object, so an entry we could not check belongs
	 * here rather than in a parallel channel the proceed gate would have to learn about separately. A
	 * PREDICATE rather than a latched flag on purpose: it must clear itself the moment the user fixes
	 * the field, so a transient lookup failure does not require reopening the dialog.
	 */
	registerUnresolvedProbe(probe: () => boolean): () => void;
	/** TRUE while any registered renderer reports an entry it could not resolve. */
	readonly hasUnresolvedEntries: boolean;
	/**
	 * Register work that must happen BEFORE `clear` drops its tracking, and get back an unregister.
	 *
	 * Dropping a commit stops the barrier waiting on it; it does not stop the commit. Renderers hang
	 * their context invalidation here so a commit that is no longer tracked also cannot still WRITE —
	 * without it, closing a dialog that stays mounted leaves a live commit with every one of its
	 * post-await guards intact, free to land in the reopened form with nothing tracking it.
	 */
	onClear(handler: () => void): () => void;
	/**
	 * TRUE while a commit the drain gave up on has still not settled — the one refusal a retry cannot
	 * clear, because nothing here can make that promise finish. Derived from `abandonedCommits`, not a
	 * fourth piece of state to keep in sync. Callers use it to say "reopen this dialog" instead of
	 * "try again", which is advice that can never work in that state.
	 */
	readonly stuck: boolean;
};

export function createAutocompleteCommitBarrier(): AutocompleteCommitBarrier {
	const pendingCommits = new Set<Promise<unknown>>();

	/**
	 * Commits the drain deadline gave up on that have STILL not settled.
	 *
	 * Deleting an abandoned commit from `pendingCommits` and nothing more is what made the timeout path
	 * lie: a later flush saw an empty set and reported a clean drain while that commit was still mid-fetch,
	 * so a second Run click submitted the stale value the first click had correctly refused. They are moved
	 * here instead, and removed only when they actually settle — so the barrier keeps answering "no" for
	 * exactly as long as the truth is unknown, without ever waiting on them again.
	 */
	const abandonedCommits = new Set<Promise<unknown>>();

	const MAX_DRAIN_PASSES = 10;

	/**
	 * Wall-clock bound. MAX_DRAIN_PASSES bounds re-entrant BATCHES, not time — a single never-settling
	 * commit blocks the very first await forever, so the pass count is never reached. The proceed action
	 * awaits this while holding its re-entry flag, so an unbounded wait is a permanently disabled button:
	 * strictly worse than the bug this barrier exists to fix. `fetchUserInputOptions` carries no
	 * AbortSignal, so nothing upstream can bound it either.
	 */
	const DRAIN_TIMEOUT_MS = 5000;

	/**
	 * Bumped by `clearAutocompleteCommits`. A flush that started before a reset cannot vouch for the form:
	 * the reset removes in-flight promises WITHOUT them settling, so the sets go empty and the flush would
	 * otherwise see nothing outstanding and report success.
	 */
	let resetGeneration = 0;

	/** Wakes in-flight flushes on a reset so they answer immediately instead of serving out the deadline. */
	const resetWaiters = new Set<() => void>();

	/** Invalidation hooks run before `clear` drops its tracking. See `onClear`. */
	const clearHandlers = new Set<() => void>();

	/**
	 * Commits that REJECTED since the last flush reported them.
	 *
	 * A rejection settles the promise, so Promise.allSettled drains exactly like a success and the flush
	 * used to answer "yes" — but the commit threw before writing anything, so the field keeps whatever it
	 * held before, which reads as valid and gets submitted. That is the same stale submission every
	 * ANTICIPATED failure path here avoids; the unexpected-exception path was the one gap. Counted rather
	 * than kept, because the promise is already settled and there is nothing left to wait for: the next
	 * flush reports the failure once, the caller refuses, and a retry is free to succeed.
	 */
	let unreportedCommitFailures = 0;

	/**
	 * Commits `untrack` withdrew, kept only until they settle so their rejection can be ignored.
	 *
	 * Withdrawing says "this renderer is gone and its contexts are invalidated, so this commit can no
	 * longer write anything" — which also means its failure can no longer harm anyone. Counting it was
	 * survivable while the counter self-cleared on report; now that a reported failure STAYS refused,
	 * counting a withdrawn one jams the barrier permanently on work the user already walked away from.
	 * Making the counter sticky is what turned this from a nuisance into a jam.
	 */
	const withdrawnCommits = new Set<Promise<unknown>>();

	/** Live predicates from the renderers under this dialog. See `registerUnresolvedProbe`. */
	const unresolvedProbes = new Set<() => boolean>();

	function track(commit: Promise<unknown>): void {
		// Captured at TRACK time so a rejection can be attributed to the session it belongs to. Without it a
		// dismissed dialog's commit rejecting later bumps the counter after clearAutocompleteCommits zeroed
		// it, and the next dialog's first proceed is refused for a failure that was not its own — the same
		// cross-session gate this barrier already avoids for pendingCommits.
		const trackedAtGeneration = resetGeneration;
		pendingCommits.add(commit);
		void commit
			.catch((error) => {
				if (resetGeneration === trackedAtGeneration && !withdrawnCommits.has(commit)) {
					unreportedCommitFailures += 1;
				}
				// The real boundary for anything commitTypedAutocompleteValue did not anticipate: a TypeError on
				// an unexpected option shape, a bug in findUnambiguousOption/applyUnambiguousMatch. Its own named
				// exits all log, so swallowing here was the single hole in that coverage.
				console.error("[autocomplete-commit] Unexpected error during commit", error);
			})
			.finally(() => {
				// Both sets: a commit that was abandoned and then settled anyway is no longer unknown, so it must
				// stop forcing a "no" verdict.
				pendingCommits.delete(commit);
				abandonedCommits.delete(commit);
				withdrawnCommits.delete(commit);
			});
	}

	/**
	 * Resolve once no commit is in flight. Returns FALSE if it cannot vouch for the form — the caller must
	 * not read it in that case.
	 *
	 * Loops because draining one commit can start another: a dependent field's refetch, or a second field
	 * blurring as focus moves to the button.
	 */
	async function flush(): Promise<boolean> {
		const resetGenerationAtEntry = resetGeneration;
		if (abandonedCommits.size > 0) {
			console.warn(`[autocomplete-commit] Cannot vouch: Reason=abandonedCommitStillUnsettled, Abandoned=${abandonedCommits.size}`);
			return false;
		}
		let deadlineTimer: ReturnType<typeof setTimeout> | undefined;
		let expired = false;
		/**
		 * Who was actually IN the window when it closed.
		 *
		 * The deadline is one wall-clock bound for the whole flush, and resolves once. So on any pass after
		 * it fires, `Promise.race` below returns immediately — and a commit tracked LATE (draining one
		 * commit can start another, cascading a dependent refetch) would be judged against a deadline that
		 * had already expired before it existed, and abandoned with effectively zero wait. That is not a
		 * cosmetic misattribution: `abandonedCommits` only drains via clear() or the commit settling, and
		 * `stuck` plus the data-destroying "close and reopen this dialog" copy are derived from it, so the
		 * barrier latched into its most destructive state far more readily than the 5s budget implies.
		 *
		 * Snapshotting at expiry rather than re-arming the deadline per pass is deliberate: re-arming would
		 * make the real bound MAX_DRAIN_PASSES * DRAIN_TIMEOUT_MS, and the comment on DRAIN_TIMEOUT_MS
		 * exists precisely because passes bound batches, not time.
		 *
		 * NO BEHAVIOURAL TEST, deliberately flagged rather than faked. I could not construct a reachable
		 * interleaving: the pass that OBSERVES expiry abandons and returns whenever anything is still
		 * pending, so the loop never continues to a pass that would meet an already-fired deadline. Two
		 * constructions (a late settle, and a mid-flush untrack) plus instrumentation both reported
		 * TrackedAfterExpiry=0, with the abandon happening on the expiry pass itself. Kept anyway because
		 * the narrowing is provably safe — it only ever abandons FEWER commits — and if the analysis is
		 * wrong the failure mode is the destructive "close and reopen" path, i.e. lost user work. The
		 * TrackedAfterExpiry counter in the log below is what would prove reachability in the field.
		 */
		let commitsInWindowAtExpiry: Set<Promise<unknown>> | null = null;
		const wake = () => undefined;
		const deadline = new Promise<void>((resolve) => {
			deadlineTimer = setTimeout(() => {
				expired = true;
				commitsInWindowAtExpiry = new Set(pendingCommits);
				resolve();
			}, DRAIN_TIMEOUT_MS);
		});
		// Raced alongside the deadline so a reset answers at once. Without it, closing the dialog mid-flush
		// left `proceeding` true — and the primary button disabled — for the rest of the 5s window, because
		// the dropped commits never settle and only the deadline could end the wait.
		let notifyReset: () => void = wake;
		const resetSignal = new Promise<void>((resolve) => {
			notifyReset = resolve;
		});
		resetWaiters.add(notifyReset);
		try {
			for (let pass = 0; pass < MAX_DRAIN_PASSES && pendingCommits.size > 0; pass++) {
				const batch = [...pendingCommits];
				await Promise.race([Promise.allSettled(batch), deadline, resetSignal]);
				if (resetGeneration !== resetGenerationAtEntry) {
					// Reset while we were waiting. The promises were dropped rather than settled, so we know
					// nothing about whether they landed — and silence here reads as success to the caller.
					console.warn(`[autocomplete-commit] Cannot vouch: Reason=resetDuringFlush, StillTracked=${pendingCommits.size}`);
					return false;
				}
				const stillPending = batch.filter((commit) => pendingCommits.has(commit));
				if (expired && stillPending.length > 0) {
					// Abandoned, scoped to THIS batch — never a blanket clear. Closing a dialog does not cancel an
					// in-flight flush, so this can fire after the user dismissed the stuck dialog, reopened it and
					// pasted again; clearing everything would untrack that NEW commit.
					//
					// And scoped further, to commits that were actually in the window when it closed. One tracked
					// after expiry never had any of the 5s, so condemning it would be arbitrary — and expensive,
					// because abandonment is what latches `stuck`. It stays tracked instead and gets a real
					// window on the next flush. This flush still cannot vouch either way.
					const timedOut = stillPending.filter((commit) => commitsInWindowAtExpiry?.has(commit) ?? true);
					const trackedAfterExpiry = stillPending.length - timedOut.length;
					for (const commit of timedOut) {
						pendingCommits.delete(commit);
						abandonedCommits.add(commit);
					}
					console.warn(
						`[autocomplete-commit] Cannot vouch: Reason=timeout, TimeoutMs=${DRAIN_TIMEOUT_MS}, Abandoned=${timedOut.length}, ` +
							`TrackedAfterExpiry=${trackedAfterExpiry}, StillTracked=${pendingCommits.size}`
					);
					return false;
				}
			}
		} finally {
			if (deadlineTimer !== undefined) clearTimeout(deadlineTimer);
			resetWaiters.delete(notifyReset);
		}
		if (pendingCommits.size > 0) {
			console.warn(
				`[autocomplete-commit] Cannot vouch: Reason=passes, Passes=${MAX_DRAIN_PASSES}, StillTracked=${pendingCommits.size}`
			);
			return false;
		}
		if (resetGeneration !== resetGenerationAtEntry || abandonedCommits.size > 0) {
			console.warn(
				`[autocomplete-commit] Cannot vouch: Reason=stateChangedDuringFlush, Abandoned=${abandonedCommits.size}, StillTracked=${pendingCommits.size}`
			);
			return false;
		}
		// Checked AFTER the drain, because a commit still in flight may be about to resolve the very entry
		// this would refuse for. An entry we could not CHECK is not a form we can vouch for: the value on
		// screen is not the value in the model, and proceeding submits the one the user replaced. Kept out
		// of `stuck` deliberately — this one is recoverable by fixing the field, so it must not tell the
		// user to reopen the dialog.
		if (anyUnresolved()) {
			console.warn("[autocomplete-commit] Cannot vouch: Reason=unresolvedEntry");
			return false;
		}
		if (unreportedCommitFailures > 0) {
			// NOT cleared on report. It used to be, with the reasoning that "a retry is free to succeed" —
			// and the retry is precisely what the UI tells the user to do, so the second click sailed through
			// on a form the first click had correctly refused. An unanticipated exception aborts
			// commitTypedAutocompleteValue BEFORE it either writes the resolved value or clears the
			// contradicted one, so the field keeps the value the user typed over. That reads as valid and
			// gets submitted: the wrong record, silently, which is the class this whole barrier exists to
			// stop. Waiting cannot fix it, so it stays refused until clear() — dialog close or reset — the
			// same terms as an abandoned commit.
			console.warn(`[autocomplete-commit] Cannot vouch: Reason=commitThrew, Failures=${unreportedCommitFailures}`);
			return false;
		}
		return true;
	}

	/**
	 * Drop tracked commits on teardown. `pendingCommits` is module-level, so a commit left in flight by a
	 * closed dialog would otherwise be waited on by the NEXT dialog's first proceed. Dropping the reference
	 * does not cancel the request — nothing can, there is no AbortSignal — it only stops a dead dialog's
	 * work from gating a live one. Abandoned commits are dropped too: they belong to the dialog that is
	 * going away, and the renderer's own reset invalidates anything they might still try to write.
	 */
	function onClear(handler: () => void): () => void {
		clearHandlers.add(handler);
		return () => clearHandlers.delete(handler);
	}

	function registerUnresolvedProbe(probe: () => boolean): () => void {
		unresolvedProbes.add(probe);
		return () => unresolvedProbes.delete(probe);
	}

	function anyUnresolved(): boolean {
		for (const probe of unresolvedProbes) {
			if (probe()) return true;
		}
		return false;
	}

	function untrack(commit: Promise<unknown>): void {
		pendingCommits.delete(commit);
		abandonedCommits.delete(commit);
		// Recorded, not just dropped. Dropping stops the barrier WAITING on it; this stops a later
		// rejection being counted against a form that no longer exists.
		withdrawnCommits.add(commit);
	}

	function clear(): void {
		// FIRST, before anything is dropped. A dropped commit is no longer tracked, so after this point
		// nothing downstream can refuse its write — invalidating here is what makes the drop safe, and is
		// the claim this module's comments have been making without any code behind it.
		for (const handler of clearHandlers) {
			handler();
		}
		resetGeneration++;
		unreportedCommitFailures = 0;
		pendingCommits.clear();
		abandonedCommits.clear();
		withdrawnCommits.clear();
		for (const notify of resetWaiters) {
			notify();
		}
	}

	return {
		track,
		flush,
		clear,
		onClear,
		untrack,
		registerUnresolvedProbe,
		get hasUnresolvedEntries() {
			return anyUnresolved();
		},
		get stuck() {
			// A thrown commit belongs here alongside an abandoned one: both mean the barrier cannot vouch
			// and waiting will not change that, so both must produce the "reopen this dialog" copy rather
			// than "give it a moment and try again". Reviewers raised the misleading message twice before
			// this became reachable state; it is one condition, not two.
			return abandonedCommits.size > 0 || unreportedCommitFailures > 0;
		}
	};
}

export const AUTOCOMPLETE_COMMIT_BARRIER: InjectionKey<AutocompleteCommitBarrier> = Symbol("autocompleteCommitBarrier");

/**
 * For a DIALOG: own one barrier and share it with every renderer mounted underneath, however many the
 * `v-for` produces. The dialog is the right owner because it is what proceeds, what closes, and what
 * the user perceives as one form.
 */
export function provideAutocompleteCommitBarrier(): AutocompleteCommitBarrier {
	const barrier = createAutocompleteCommitBarrier();
	provide(AUTOCOMPLETE_COMMIT_BARRIER, barrier);
	return barrier;
}

/**
 * For a RENDERER: the hosting dialog's barrier, or a private one when mounted standalone.
 *
 * The fallback logs rather than staying quiet. A renderer that falls back is tracking commits into a
 * barrier NOBODY flushes, so its host's proceed would vouch for a form still mid-commit — silently,
 * and looking exactly like a clean drain. That is the failure this whole module exists to catch, so it
 * gets the same treatment as every other one here (root `CLAUDE.md`, "Diagnostic logging when fixing
 * silent failures") instead of being left to be discovered as a stale submission.
 */
export function useAutocompleteCommitBarrier(): AutocompleteCommitBarrier {
	return inject(
		AUTOCOMPLETE_COMMIT_BARRIER,
		() => {
			console.warn("[autocomplete-commit] No host barrier provided: commits tracked here are flushed by nobody");
			return createAutocompleteCommitBarrier();
		},
		true
	);
}
